import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  LayoutDashboard, FileBarChart2, PackageOpen, Zap, ScrollText, Boxes,
  Users, LogOut, Workflow, Grid3x3, Wrench, Inbox, Warehouse, Calculator, Beaker, Recycle, Bell, ClipboardList, ClipboardCheck
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", module: "dashboard" },
  { to: "/dpr", label: "Production (DPR)", icon: FileBarChart2, testId: "nav-dpr", module: "dpr" },
  { to: "/stock", label: "Stock", icon: PackageOpen, testId: "nav-stock", module: "stock" },
  { to: "/scrap", label: "Scrap Tracker", icon: Recycle, testId: "nav-scrap", module: "scrap" },
  { to: "/power", label: "Power", icon: Zap, testId: "nav-power", module: "power" },
  { to: "/inward-outward", label: "Inward / Outward", icon: ScrollText, testId: "nav-gatepass", module: "gatepass" },
  { to: "/electrolysis", label: "Electrolysis", icon: Beaker, testId: "nav-electrolysis", module: "electrolysis" },
  { to: "/buffing", label: "Buffing (Job Work)", icon: Wrench, testId: "nav-buffing", module: "buffing" },
  { to: "/approvals", label: "Approvals", icon: Inbox, testId: "nav-approvals", module: "approvals" },
  { to: "/audit", label: "Audit", icon: ClipboardCheck, testId: "nav-audit", module: "audit" },
];

const ADMIN_NAV = [
  { to: "/processes", label: "Process Master", icon: Workflow, testId: "nav-processes", module: "processes" },
  { to: "/inventory", label: "Inventory + BOM", icon: Warehouse, testId: "nav-inventory", module: "inventory" },
  { to: "/bom", label: "Process Scrap", icon: Grid3x3, testId: "nav-bom", module: "bom" },
  { to: "/cost-sheet", label: "Cost Sheet", icon: Calculator, testId: "nav-cost-sheet", module: "cost-sheet" },
];

const navClass = ({ isActive }, accent = "blue") =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
    isActive ? `bg-slate-800 text-white border-l-4 border-${accent}-500 pl-2` : "text-slate-300 hover:bg-slate-800/60 hover:text-white"}`;

export default function Sidebar() {
  const { user, logout, isSuper, canRead } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isBackOffice = isSuper || user?.role === "admin";
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [notif, setNotif] = useState({ logins: [], unread: 0 });
  const [showNotif, setShowNotif] = useState(false);
  const notifRef = useRef(null);
  const onLogout = async () => { await logout(); navigate("/login"); };

  useEffect(() => {
    if (!isBackOffice) return;
    let active = true;
    const poll = async () => {
      try {
        const [ap, nt] = await Promise.all([
          api.get("/approvals", { params: { status: "pending" } }),
          api.get("/notifications"),
        ]);
        if (active) { setPendingApprovals(Array.isArray(ap.data) ? ap.data.length : 0); setNotif(nt.data); }
      } catch (e) { console.debug("Sidebar poll failed (ignored):", e?.message); }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { active = false; clearInterval(t); };
  }, [isBackOffice, location.pathname]);

  const openNotif = async () => {
    setShowNotif((v) => !v);
    if (!showNotif && notif.unread > 0) {
      try { await api.post("/notifications/seen"); setNotif((n) => ({ ...n, unread: 0 })); } catch (e) { /* ignore */ }
    }
  };

  const mainNav = NAV.filter((n) => canRead(n.module));
  const adminNav = ADMIN_NAV.filter((n) => canRead(n.module));

  return (
    <aside data-testid="sidebar" className="w-64 shrink-0 bg-slate-900 text-slate-200 flex flex-col h-screen sticky top-0">
      <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
        <img src="/back-at-one-white.png" alt="Back at One" className="h-14 w-auto object-contain" data-testid="sidebar-logo" />
        {isBackOffice && (
          <div className="relative" ref={notifRef}>
            <button data-testid="notif-bell" onClick={openNotif} className="relative p-2 rounded-lg hover:bg-slate-800 text-slate-300">
              <Bell size={18} />
              {notif.unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{notif.unread > 9 ? "9+" : notif.unread}</span>}
            </button>
            {showNotif && (
              <div data-testid="notif-panel" className="absolute right-0 mt-2 w-72 max-h-96 overflow-y-auto bg-white text-slate-800 rounded-xl shadow-2xl border border-slate-200 z-50">
                <div className="px-4 py-2.5 border-b font-semibold text-sm flex items-center justify-between">User Logins <NavLink to="/log" onClick={() => setShowNotif(false)} className="text-xs text-blue-600 font-medium">View all</NavLink></div>
                {notif.logins.length === 0 && <div className="px-4 py-6 text-center text-sm text-slate-400">No logins yet.</div>}
                {notif.logins.slice(0, 15).map((l) => (
                  <div key={l.id} className="px-4 py-2 border-b border-slate-50 text-sm">
                    <div className="font-medium text-slate-700">{l.user_email}</div>
                    <div className="text-xs text-slate-400">{new Date(l.ts).toLocaleString()} · {l.summary}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {mainNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testId} className={(s) => navClass(s, "blue")}>
            <n.icon size={18} />
            <span className="font-medium">{n.label}</span>
            {n.module === "approvals" && isBackOffice && pendingApprovals > 0 && (
              <span data-testid="approvals-pending-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-slate-900 text-xs font-bold">{pendingApprovals > 99 ? "99+" : pendingApprovals}</span>
            )}
          </NavLink>
        ))}
        {(adminNav.length > 0 || isSuper) && (
          <>
            <div className="px-3 pt-4 pb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">{isBackOffice ? "Admin" : "Reference"}</div>
            {adminNav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testId} className={(s) => navClass(s, "amber")}>
                <n.icon size={18} />
                <span className="font-medium">{n.label}</span>
              </NavLink>
            ))}
            {isBackOffice && (
              <NavLink to="/log" data-testid="nav-log" className={(s) => navClass(s, "amber")}>
                <ClipboardList size={18} />
                <span className="font-medium">Log</span>
              </NavLink>
            )}
            {isSuper && (
              <NavLink to="/users" data-testid="nav-users" className={(s) => navClass(s, "amber")}>
                <Users size={18} />
                <span className="font-medium">Users &amp; Roles</span>
              </NavLink>
            )}
          </>
        )}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="text-sm text-slate-200 font-medium">{user?.name || "—"}</div>
        <div className="text-xs text-slate-400 capitalize">{user?.role || ""}</div>
        <button data-testid="logout-btn" onClick={onLogout} className="mt-3 w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white text-sm py-2 rounded-lg transition-colors">
          <LogOut size={16} /> Logout
        </button>
      </div>
    </aside>
  );
}
